import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  AddIconComponent,
  LockIconComponent,
  SmsIconComponent,
  TickSquareIconComponent,
  CloseSquareIconComponent,
  UserCancelIconComponent,
  DeleteIconComponent,
} from '@kuiper/icons';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { UserInsight, UserList } from '../user-model';
import { UserService } from '../user.service';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  USERS_DATABASE_COLUMNS_SORT,
  USERS_DATABASE_VIEW_DETAILS_ACTION,
  USERS_DATABASE_EDIT_ACTION,
  USERS_DATABASE_REINVITE_ACTION,
  USERS_DATABASE_SEND_INVITATION_ACTION,
  USERS_DATABASE_ACTIVE_ACTIONS,
  USERS_DATABASE_INACTIVE_ACTIONS,
  USERS_DATABASE_FILTER_TABS,
  USERS_DATABASE_CANCEL_INVITATION_ACTION,
  USERS_DATABASE_DELETE_ACTION,
  USERS_DATABASE_REMOVE_EMPLOYEE_ROLE_ACTION,
  USERS_DATABASE_GRANT_EMPLOYEE_ROLE_ACTION,
} from './users-list.constant';
import { UserActionKey } from './user-list.enums';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { StatComponent } from '../../../../../../ui-components/lib/stat/stat.component';
import { StatIconName } from '../../../../../../ui-components/lib/stat/stat.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { TableComponent } from '../../../../../../ui-components/lib/table/table.component';
import { TruncatePipe } from '../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { ENDPOINTS } from '../../../../../common/constants/endpoints.constants';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { ActionList } from '../../../../../common/models/action-list.model';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { RoleTypes } from '../../../../../common/constants/role-types.constant';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { Status } from '../../../../../common/enums/user-status';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FilterTableColByFeatFlagPipe } from '../../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { SearchEnum } from '../../../../../common/enums/search.enum';
import { SearchFieldComponent } from '../../../../../../ui-components/lib/search-field/search-field.component';
import {
  initialSearchTerm,
  SearchService,
} from '../../../../../../ui-components/lib/search-field/search.service';
import { InsightsPanelComponent } from '../../../../../common/components/insights-panel/insights-panel.component';
import {
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { debounceTime, distinctUntilChanged, finalize, skip } from 'rxjs';

@Component({
  selector: 'lib-users-database-list',
  standalone: true,
  imports: [
    DatePipe,
    StatComponent,
    TableComponent,
    BadgeComponent,
    PaginatorComponent,
    TranslocoPipe,
    ActionListComponent,
    RouterLink,
    UserStatusPipe,
    OverlayLoaderComponent,
    ReactiveFormsModule,
    PaginatedSelectComponent,
    ValidationMessageComponent,
    AvatarComponent,
    TruncatePipe,
    AddIconComponent,
    CloseSquareIconComponent,
    LockIconComponent,
    SmsIconComponent,
    TickSquareIconComponent,
    TenantPathPipe,
    UserCancelIconComponent,
    DeleteIconComponent,
    NgTemplateOutlet,
    FilterTableColByFeatFlagPipe,
    SearchFieldComponent,
    InsightsPanelComponent,
  ],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UsersDatabaseListComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  readonly modalService = inject(ModalServiceService);
  userService = inject(UserService);
  router = inject(Router);
  transloco = inject(TranslocoService);
  tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the panel header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly toasterService = inject(ToasterService);
  private readonly searchService = inject(SearchService);

  isEmployeeVendorPortalEnabled = computed<boolean>(() =>
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.portal_settings
    )
  );

  isUserPlacementTypesEnabled = computed<boolean>(() =>
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.user_placement_types
    )
  );

  shouldSkipReplacementOnDeactivate = computed(() => {
    if (!this.isEmployeeVendorPortalEnabled()) {
      return false;
    }

    return this.isVendorOnlyUser(this.selectedUser()?.roles);
  });

  readonly REPLACE_USER_MODAL_MODE = {
    DEACTIVATE: 'deactivate',
    CANCEL_INVITATION: 'cancel_invitation',
    DELETE: 'delete',
    REMOVE_EMPLOYEE_ROLE: 'remove_employee_role',
  } as const;

  private readonly deletableUserStatuses: string[] = [
    Status.CANCELLED,
    Status.INACTIVE,
    Status.EXPIRED,
  ];

  deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  deactivateVendorOnlyModal = viewChild<TemplateRef<unknown>>(
    'deactivateVendorOnly'
  );
  activateModal = viewChild<TemplateRef<unknown>>('activate');
  reinviteModal = viewChild<TemplateRef<unknown>>('reinvite');
  resetPasswordModal = viewChild<TemplateRef<unknown>>('resetpassword');
  sendInvitationModal = viewChild<TemplateRef<unknown>>('sendInvitation');
  cancelInvitationModal = viewChild<TemplateRef<unknown>>('cancelInvitation');
  deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');
  deleteExpiredModal = viewChild<TemplateRef<unknown>>('deleteExpired');
  removeEmployeeRoleModal =
    viewChild<TemplateRef<unknown>>('removeEmployeeRole');
  grantEmployeeRoleModal = viewChild<TemplateRef<unknown>>('grantEmployeeRole');

  isLoading = signal<boolean>(true);
  isInsightsLoading = signal<boolean>(true);
  isReinviteUser = signal<boolean>(false);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);
  totalCount = signal<number>(0);
  insights = signal<UserInsight[]>([]);
  Router = inject(Router);
  deactivateUserFromGroup!: FormGroup;
  selectedUser = signal<any>('');
  userLookupListApi = '';
  allColumns = computed<TableColumns[]>(() =>
    USERS_DATABASE_COLUMNS_SORT.filter(
      col =>
        (col.identifier !== 'portal_access' ||
          this.isEmployeeVendorPortalEnabled()) &&
        (col.identifier !== 'placement_type' ||
          this.isUserPlacementTypesEnabled())
    ).map((col, index) => {
      const translatedCol = {
        ...col,
        header: col.header ? this.transloco.translate(col.header) : '',
      };

      if (index === 1) {
        return {
          ...translatedCol,
          showViewIcon: true,
        };
      }
      return translatedCol;
    })
  );

  defaultSort = signal<{ key: string; value: 'asc' | 'desc' }>({
    key: '',
    value: 'asc',
  });
  activeSort = signal<{ key: string; value: 'asc' | 'desc' }>(
    this.defaultSort()
  );
  userList = signal<UserList[]>([]);
  usersWithPortalAccess = computed(() =>
    this.userList().map(user => ({
      ...user,
      portalAccessLabel: this.getPortalAccessLabel(user.roles),
    }))
  );
  viewDetailsAction = signal<ActionList>({
    ...USERS_DATABASE_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(USERS_DATABASE_VIEW_DETAILS_ACTION.label),
  });
  editAction = signal<ActionList>({
    ...USERS_DATABASE_EDIT_ACTION,
    label: this.transloco.translate(USERS_DATABASE_EDIT_ACTION.label),
  });
  reinviteAction = signal<ActionList>({
    ...USERS_DATABASE_REINVITE_ACTION,
    label: this.transloco.translate(USERS_DATABASE_REINVITE_ACTION.label),
  });
  sendInvitationAction = signal<ActionList>({
    ...USERS_DATABASE_SEND_INVITATION_ACTION,
    label: this.transloco.translate(
      USERS_DATABASE_SEND_INVITATION_ACTION.label
    ),
  });
  cancelInvitationAction = signal<ActionList>({
    ...USERS_DATABASE_CANCEL_INVITATION_ACTION,
    label: this.transloco.translate(
      USERS_DATABASE_CANCEL_INVITATION_ACTION.label
    ),
  });
  deleteAction = signal<ActionList>({
    ...USERS_DATABASE_DELETE_ACTION,
    label: this.transloco.translate(USERS_DATABASE_DELETE_ACTION.label),
  });

  activeActions = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    ...USERS_DATABASE_ACTIVE_ACTIONS.map(action => ({
      ...action,
      label: this.transloco.translate(action.label),
    })),
  ]);
  inactiveActions = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    ...USERS_DATABASE_INACTIVE_ACTIONS.map(action => ({
      ...action,
      label: this.transloco.translate(action.label),
    })),
  ]);
  pendingActions = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    this.reinviteAction(),
  ]);
  expiredActions = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    this.reinviteAction(),
  ]);
  cancelledActions = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    this.reinviteAction(),
  ]);
  draftAction = signal<ActionList[]>([
    this.viewDetailsAction(),
    this.editAction(),
    this.sendInvitationAction(),
  ]);
  removeEmployeeRoleAction = signal<ActionList>({
    ...USERS_DATABASE_REMOVE_EMPLOYEE_ROLE_ACTION,
    label: this.transloco.translate(
      USERS_DATABASE_REMOVE_EMPLOYEE_ROLE_ACTION.label
    ),
  });
  grantEmployeeRoleAction = signal<ActionList>({
    ...USERS_DATABASE_GRANT_EMPLOYEE_ROLE_ACTION,
    label: this.transloco.translate(
      USERS_DATABASE_GRANT_EMPLOYEE_ROLE_ACTION.label
    ),
  });

  tableFilterTabs = signal<TableFilterTab[]>(
    USERS_DATABASE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  activeTableFilterTab = signal<TableFilterTab>(this.tableFilterTabs()[0]);

  isSubmitting = signal<boolean>(false);
  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal(initialSearchTerm());
  isMoreThanThreeCharacters = signal(
    this.searchService.isMinLength(this.searchTerm())
  );
  private readonly searchTerm$ = toObservable(this.searchTerm);

  ngOnInit() {
    this.getList();
    this.getInsights();
    this.initFormGroup();
    this.addCancelActionBasedOnFeatureFlag();
    this.searchTermWrapper();
  }

  searchTermWrapper() {
    this.searchTerm$
      .pipe(
        skip(1),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => {
        this.handleSearchTerm(term);
      });
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      this.getList();
    }
  }

  addCancelActionBasedOnFeatureFlag() {
    this.pendingActions.update(actions => [
      ...actions,
      this.cancelInvitationAction(),
    ]);
  }

  initFormGroup() {
    this.deactivateUserFromGroup = new FormGroup({
      replacementUserId: new FormControl('', Validators.required),
    });
  }

  getStatusActionList(status: string, id: number, roles?: UserList['roles']) {
    this.viewDetailsAction().routerLinks = [
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.USERS_DATABASE.DETAILS}/${id}`
      ),
    ];
    this.editAction().routerLinks = [
      this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.FORM}/${id}`),
    ];

    let actions: ActionList[];
    switch (status) {
      case Status.ACTIVE:
        actions = this.activeActions();
        break;
      case Status.PENDING:
        actions = this.pendingActions();
        break;
      case Status.INACTIVE:
        actions = this.inactiveActions();
        break;
      case Status.DRAFT:
        actions = this.draftAction();
        break;
      case Status.CANCELLED:
        actions = this.cancelledActions();
        break;
      default:
        actions = this.expiredActions();
        break;
    }

    const employeePortalAction = this.getEmployeePortalAction(status, roles);
    if (employeePortalAction) {
      actions = [...actions, employeePortalAction];
    }

    if (this.canDeleteUser(status)) {
      return [...actions, this.deleteAction()];
    }

    return actions;
  }

  private getEmployeePortalAction(
    status: string,
    roles?: UserList['roles']
  ): ActionList | null {
    if (!this.isEmployeeVendorPortalEnabled() || status !== Status.ACTIVE) {
      return null;
    }

    const hasVendor = this.hasRole(roles, RoleTypes.VENDOR);
    const hasEmployee = this.hasRole(roles, RoleTypes.EMPLOYEE);

    if (hasVendor && hasEmployee) {
      return this.removeEmployeeRoleAction();
    }

    if (hasVendor || roles?.length === 0) {
      return this.grantEmployeeRoleAction();
    }

    return null;
  }

  private hasRole(
    roles: UserList['roles'] | undefined,
    roleName: string
  ): boolean {
    return roles?.some(role => role.name === roleName) ?? false;
  }

  private isVendorOnlyUser(roles?: UserList['roles']): boolean {
    return (
      this.hasRole(roles, RoleTypes.VENDOR) &&
      !this.hasRole(roles, RoleTypes.EMPLOYEE)
    );
  }

  canDeleteUser(status: string): boolean {
    if (
      !this.featureFlagService.getFeatureFlagValue(
        FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.delete_users
      )
    ) {
      return false;
    }

    return this.deletableUserStatuses.includes(status);
  }

  actionListItemClick(option: any, user: any) {
    this.selectedUser.set(user);
    this.userLookupListApi = `${ENDPOINTS.Lookup.User}?filters[not_states][0]=${Status.INACTIVE}&filters[not_states][1]=${Status.CANCELLED}&filters[not_states][2]=${Status.DELETED}&filters[not_id]=${this.selectedUser()?.id}`;
    switch (option) {
      case UserActionKey.DEACTIVATE:
        if (!this.shouldSkipReplacementOnDeactivate()) {
          this.initFormGroup();
        }
        this.openModal('deactivate');
        break;
      case UserActionKey.ACTIVATE:
        this.openModal('activate');
        break;
      case UserActionKey.REINVITE:
        this.openModal('reinvite');
        break;
      case UserActionKey.RESET_PASSWORD:
        this.openModal('resetpassword');
        break;
      case UserActionKey.SEND_INVITATION:
        this.openModal('sendInvitation');
        break;
      case UserActionKey.CANCEL_INVITATION:
        this.openModal('cancelInvitation');
        break;
      case UserActionKey.DELETE:
        if (user.state?.value === Status.EXPIRED) {
          this.initFormGroup();
        }
        this.openModal('delete');
        break;
      case UserActionKey.REMOVE_EMPLOYEE_ROLE:
        this.initFormGroup();
        this.openModal('removeEmployeeRole');
        break;
      case UserActionKey.GRANT_EMPLOYEE_ROLE:
        this.openModal('grantEmployeeRole');
        break;
    }
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  openModal(type: string) {
    let modalType;
    switch (type) {
      case 'deactivate':
        modalType = this.shouldSkipReplacementOnDeactivate()
          ? this.deactivateVendorOnlyModal()
          : this.deactivateModal();
        break;
      case 'activate':
        modalType = this.activateModal();
        break;
      case 'reinvite':
        modalType = this.reinviteModal();
        break;
      case 'resetpassword':
        modalType = this.resetPasswordModal();
        break;
      case 'sendInvitation':
        modalType = this.sendInvitationModal();
        break;
      case 'cancelInvitation':
        modalType = this.cancelInvitationModal();
        break;
      case 'delete':
        modalType =
          this.selectedUser()?.state?.value === Status.EXPIRED
            ? this.deleteExpiredModal()
            : this.deleteModal();
        break;
      case 'removeEmployeeRole':
        modalType = this.removeEmployeeRoleModal();
        break;
      case 'grantEmployeeRole':
        modalType = this.grantEmployeeRoleModal();
        break;
    }
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: modalType,
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  deactivateUser = () => {
    this.isSubmitting.set(true);
    const replacementUserId = this.shouldSkipReplacementOnDeactivate()
      ? undefined
      : (<FormControl>this.deactivateUserFromGroup.get('replacementUserId'))
          .value;

    this.userService
      .deactivateUser(this.selectedUser().id, replacementUserId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting.set(false);
          this.closeModal();
        })
      )
      .subscribe({
        next: () => {
          this.getList();
          this.getInsights();
        },
      });
  };

  activateUser() {
    this.userService
      .activateUser(this.selectedUser().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => {
          this.closeModal();
          // The interceptor toasts every non-422; a 422 (e.g. the plan-limit refusal) would otherwise be silent.
          if (error?.errors) {
            this.toasterService.add(error.message, '', '', 'error', true, true);
          }
        },
        complete: () => {
          this.closeModal();
          this.getList();
          this.getInsights();
        },
      });
  }

  grantEmployeeAccess() {
    this.userService
      .grantEmployeeAccess(this.selectedUser().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.closeModal();
        },
        complete: () => {
          this.closeModal();
          this.getList();
          this.getInsights();
        },
      });
  }

  removeEmployeeAccess = () => {
    this.isSubmitting.set(true);
    this.userService
      .removeEmployeeAccess(
        this.selectedUser().id,
        (<FormControl>this.deactivateUserFromGroup.get('replacementUserId'))
          .value
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting.set(false);
          this.closeModal();
        })
      )
      .subscribe({
        next: () => {
          this.getList();
          this.getInsights();
        },
      });
  };

  resetPassword() {
    this.userService
      .resetPassword(this.selectedUser().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.closeModal();
        },
        complete: () => {
          this.closeModal();
        },
      });
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getList();
  }

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set(this.defaultSort());
    }
    this.currentPage.set(1);
    this.getList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getList();
  }

  getList() {
    this.isLoading.set(true);
    this.totalCount.set(0);
    this.userService
      .getUsersList(
        this.currentPage(),
        this.currentSize(),
        this.activeTableFilterTab().key,
        this.activeSort(),
        this.searchService.checkSearchLength(this.searchTerm())
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<UserList>) => {
          this.userList.set(
            res.data.map(user => {
              user.last_accessed_at = this.convertDateToLocal(
                user.last_accessed_at
              );
              return user;
            })
          );
          this.totalPages.set(res.meta.last_page);
          this.totalCount.set(res.meta.total);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  convertDateToLocal(dateString: string): string {
    return dateString ? dateString.replace(' ', 'T') + 'Z' : '';
  }

  getInsights() {
    this.userService
      .getInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.insights.set(res.data);
          this.isInsightsLoading.set(false);
        },
      });
  }

  private getPortalAccessLabel(roles?: UserList['roles']): string {
    if (!roles || roles.length === 0) {
      return '';
    }
    const hasVendor = this.hasRole(roles, RoleTypes.VENDOR);
    const hasEmployee = this.hasRole(roles, RoleTypes.EMPLOYEE);

    if (hasVendor && hasEmployee) {
      return 'users_database_screen_employee_and_vendor_role_label';
    }

    if (hasVendor) {
      return 'users_database_screen_vendor_role_label';
    }

    return 'users_database_screen_employee_role_label';
  }

  getInsightIconName(key: string): StatIconName {
    let iconName: StatIconName = 'info';
    switch (key) {
      case 'all_users':
        iconName = 'user';
        break;
      case 'active':
        iconName = 'tick_square';
        break;
      case 'inactive':
        iconName = 'forbidden';
        break;
    }
    return iconName;
  }

  reinviteUser() {
    this.isReinviteUser.set(true);
    this.userService
      .reInviteUser(this.selectedUser().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isReinviteUser.set(false);
          this.closeModal();
          this.getList();
        },
        error: error => {
          this.isReinviteUser.set(false);
          // The interceptor toasts every non-422; a 422 (e.g. the plan-limit refusal) would otherwise be silent.
          if (error?.errors) {
            this.toasterService.add(error.message, '', '', 'error', true, true);
          }
        },
      });
  }

  cancelUserInvitation = () => {
    this.isSubmitting.set(true);
    this.userService
      .cancelInvitation(
        this.selectedUser().id,
        (<FormControl>this.deactivateUserFromGroup.get('replacementUserId'))
          .value
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.isSubmitting.set(false);
          this.closeModal();
        },
        complete: () => {
          this.isSubmitting.set(false);
          this.closeModal();
          this.getList();
          this.getInsights();
        },
      });
  };

  deleteUser = () => {
    this.isSubmitting.set(true);
    const replacementUserId =
      this.selectedUser()?.state?.value === Status.EXPIRED
        ? (<FormControl>this.deactivateUserFromGroup.get('replacementUserId'))
            .value
        : undefined;

    this.userService
      .deleteUser(this.selectedUser().id, replacementUserId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting.set(false);
          this.closeModal();
        })
      )
      .subscribe({
        next: () => {
          this.getList();
          this.getInsights();
        },
      });
  };

  viewUserDetails(user: any) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.USERS_DATABASE.DETAILS}/${user.id}`
      )
    );
  }

  handleTableFilterTab(tab: any) {
    this.activeTableFilterTab.set(tab);
    this.tableFilterTabs().forEach(t => {
      t.active = t.key === tab.key;
    });
    this.currentPage.set(1);
    this.getList();
  }
}
